import * as React from 'react'
import { EyeOff, Eye, MessageCircle, Send, Reply } from 'lucide-react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { FieldError } from '@/components/ui/field-error'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { useTranslations } from '@/hooks/use-translations'
import { cn } from '@/lib/utils'
import { useCommentAction, useCommentMediaQuery, useCommentsQuery } from '@/queries/meta'
import type { CommentChannel, InstagramComment, InstagramMedia } from '@/services/instagramService'
import { commentTextNamespace } from './commentTextNamespace'

type Composer = { commentId: string; kind: 'reply' | 'private-reply' } | null

function formatDate(value?: string) {
  return value ? new Date(value).toLocaleString() : ''
}

function MediaThumb({ media }: { media: InstagramMedia }) {
  const src = media.media_type === 'VIDEO' ? media.thumbnail_url : media.media_url
  if (!src) return <div className="aspect-square w-full rounded-md bg-muted" />
  return <img src={src} alt={media.caption ?? ''} className="aspect-square w-full rounded-md object-cover" />
}

function CommentRow({ channel, comment, mediaId }: { channel: CommentChannel; comment: InstagramComment; mediaId: string }) {
  const { t } = useTranslations()
  const ns = commentTextNamespace[channel]
  // Instagram names people by handle; a Facebook Page sees their name.
  const author = (name?: string) => (channel === 'instagram' ? `@${name ?? ''}` : name)
  const [composer, setComposer] = React.useState<Composer>(null)
  const [message, setMessage] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)
  const action = useCommentAction(channel, mediaId)
  const errorId = `comment-${comment.id}-error`

  const open = (kind: 'reply' | 'private-reply') => {
    setComposer({ commentId: comment.id, kind })
    setMessage('')
    setError(null)
  }

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    if (!composer) return
    if (!message.trim()) {
      setError(t(ns('comments.message_required')))
      return
    }
    action.mutate(
      { kind: composer.kind, commentId: comment.id, message: message.trim() },
      {
        onSuccess: () => {
          toast.success(t(composer.kind === 'reply' ? ns('comments.reply_sent') : ns('comments.dm_sent')))
          setComposer(null)
        },
        onError: (err) => toast.error(`${t(ns('comments.action_error'))}: ${err.message}`),
      },
    )
  }

  const toggleHidden = () => {
    const hidden = !comment.hidden
    action.mutate(
      { kind: 'hide', commentId: comment.id, hidden },
      {
        onSuccess: () => toast.success(t(hidden ? ns('comments.hidden_done') : ns('comments.unhidden_done'))),
        onError: (err) => toast.error(`${t(ns('comments.action_error'))}: ${err.message}`),
      },
    )
  }

  return (
    <li className="space-y-2 rounded-lg border p-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold">{author(comment.username)}</span>
        <span className="text-xs text-muted-foreground">{formatDate(comment.timestamp)}</span>
        {comment.hidden && <Badge variant="secondary">{t(ns('comments.hidden'))}</Badge>}
      </div>
      <p className={cn('text-sm', comment.hidden && 'text-muted-foreground')}>{comment.text}</p>

      {comment.replies?.data?.length ? (
        <ul className="space-y-1 border-l-2 pl-4">
          {comment.replies.data.map((reply) => (
            <li key={reply.id} className="text-sm">
              <span className="font-semibold">{author(reply.username)}</span> {reply.text}
            </li>
          ))}
        </ul>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <Button size="xs" variant="outline" onClick={() => open('reply')}>
          <Reply className="h-4 w-4" /> {t(ns('comments.reply'))}
        </Button>
        <Button size="xs" variant="outline" onClick={() => open('private-reply')}>
          <Send className="h-4 w-4" /> {t(ns('comments.send_dm'))}
        </Button>
        <Button size="xs" variant="ghost" onClick={toggleHidden} disabled={action.isPending}>
          {comment.hidden ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
          {t(comment.hidden ? ns('comments.unhide') : ns('comments.hide'))}
        </Button>
      </div>

      {composer && (
        <form onSubmit={submit} className="space-y-2">
          <Textarea
            aria-label={t(composer.kind === 'reply' ? ns('comments.reply') : ns('comments.send_dm'))}
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            placeholder={t(composer.kind === 'reply' ? ns('comments.reply_placeholder') : ns('comments.dm_placeholder'))}
            aria-invalid={!!error}
            aria-describedby={error ? errorId : undefined}
            rows={2}
          />
          <FieldError id={errorId}>{error}</FieldError>
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={action.isPending}>
              {t(ns('comments.send'))}
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setComposer(null)}>
              {t(ns('comments.cancel'))}
            </Button>
          </div>
        </form>
      )}
    </li>
  )
}

/** Posts and their comments, for any channel in `CommentChannel`. */
export function CommentsTab({ channel }: { channel: CommentChannel }) {
  const { t } = useTranslations()
  const ns = commentTextNamespace[channel]
  const media = useCommentMediaQuery(channel)
  const [selectedId, setSelectedId] = React.useState<string | null>(null)
  const comments = useCommentsQuery(channel, selectedId)

  React.useEffect(() => {
    if (!selectedId && media.data?.length) setSelectedId(media.data[0].id)
  }, [media.data, selectedId])

  if (media.isLoading) {
    return (
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => <Skeleton key={index} className="aspect-square w-full" />)}
      </div>
    )
  }

  if (media.error) {
    return (
      <div className="rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
        {t(ns('load_error'))}: {media.error.message}
      </div>
    )
  }

  if (!media.data?.length) {
    return (
      <EmptyState
        icon={<MessageCircle className="h-6 w-6" />}
        title={t(ns('comments.no_posts'))}
        description={t(ns('comments.no_posts_description'))}
      />
    )
  }

  return (
    <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
      <section className="space-y-4">
        <h2 className="text-lg font-semibold">{t(ns('comments.posts_title'))}</h2>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          {media.data.map((item) => (
            <Button
              key={item.id}
              type="button"
              variant="ghost"
              size="tarjeta"
              onClick={() => setSelectedId(item.id)}
              aria-pressed={selectedId === item.id}
              className={cn(
                'items-stretch gap-2 text-left',
                selectedId === item.id ? 'border border-primary bg-primary/5' : 'border border-transparent',
              )}
            >
              <MediaThumb media={item} />
              <span className="line-clamp-2 text-xs text-muted-foreground">{item.caption}</span>
              <span className="text-xs text-muted-foreground">
                {t(ns('comments.comments_count'), { count: item.comments_count ?? 0 })}
              </span>
            </Button>
          ))}
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-lg font-semibold">{t(ns('comments.comments_title'))}</h2>
        {!selectedId && <p className="text-sm text-muted-foreground">{t(ns('comments.select_post'))}</p>}
        {comments.isLoading && <Skeleton className="h-24 w-full" />}
        {comments.error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
            {t(ns('load_error'))}: {comments.error.message}
          </div>
        )}
        {comments.data && comments.data.length === 0 && (
          <p className="text-sm text-muted-foreground">{t(ns('comments.no_comments'))}</p>
        )}
        {selectedId && comments.data && comments.data.length > 0 && (
          <ul className="space-y-2">
            {comments.data.map((comment) => (
              <CommentRow key={comment.id} channel={channel} comment={comment} mediaId={selectedId} />
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}
